import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { authRequestErrorMessage } from "../auth/authRequest";
import { toast } from "sonner";
import { CheckCircle2, MapPin } from "lucide-react";
import PolicyCard from "../components/PolicyCard";
import TelematicsPanel from "../components/TelematicsPanel";
import EsimCard from "../components/EsimCard";
import BookingScheduleSummary from "../components/BookingScheduleSummary";
import { formatUsd } from "../lib/currencyDisplay";
import { pageItems } from "../lib/pageItems";
import { formatLocalTimestamp } from "../lib/dateLocal";
import { completeVehicleStripeCheckout } from "../lib/stripeCheckoutComplete";
import { inclusiveTaxesAndFees } from "../lib/bookingQuoteDisplay";
import { BOOKING_AGENT_ENABLED } from "../lib/featureFlags";

export default function BookingComplete() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const sessionId = params.get("session_id");
  const [confirmed, setConfirmed] = useState(null);
  const [confirmedEsim, setConfirmedEsim] = useState(null);
  const [vehicleLabel, setVehicleLabel] = useState("");
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);

  const applyConfirmedBooking = useCallback((data) => {
    setConfirmed(data);
    if (data?.esim) setConfirmedEsim(data.esim);
    const snap = data?.vehicle_snapshot;
    if (snap) {
      setVehicleLabel(`${snap.year || ""} ${snap.make || ""} ${snap.model || ""}`.trim());
    }
  }, []);

  const confirmPayment = useCallback(async () => {
    if (!sessionId) {
      setError("Missing payment session");
      return;
    }
    setCompleting(true);
    setError(null);
    try {
      const data = await completeVehicleStripeCheckout(API, sessionId);
      applyConfirmedBooking(data);
      if (BOOKING_AGENT_ENABLED) {
        sessionStorage.setItem(
          "zcars_agent_booking_done",
          data?.booking_id || "1",
        );
      }
      toast.success("Payment confirmed — your trip is booked");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "payment_not_completed") {
        setError(detail);
        toast.error("Payment was not completed");
      } else if (e.code === "completion_timeout") {
        const msg = e.message || "Booking confirmation is taking longer than expected. Please retry.";
        setError(msg);
        toast.error(msg);
      } else {
        setError(authRequestErrorMessage(e));
        toast.error(authRequestErrorMessage(e));
      }
    } finally {
      setCompleting(false);
    }
  }, [sessionId, applyConfirmedBooking]);

  useEffect(() => {
    if (authLoading || autoStarted) return;
    if (!sessionId) {
      setError("Missing payment session");
      return;
    }
    setAutoStarted(true);
    confirmPayment();
  }, [sessionId, authLoading, autoStarted, confirmPayment]);

  if (!sessionId && !authLoading) {
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-20 text-center" data-testid="bk-complete-error">
        <h1 className="zc-headline text-3xl">Payment not confirmed</h1>
        <p className="text-[var(--zc-text-2)] mt-3">Missing payment session</p>
        <button
          type="button"
          onClick={() => navigate(`/book/${id}`)}
          className="mt-8 px-5 h-11 zc-btn-primary text-sm"
          data-testid="bk-complete-retry"
        >
          Return to checkout
        </button>
      </div>
    );
  }

  if (authLoading || completing || (!error && !confirmed)) {
    return (
      <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20 text-[var(--zc-text-2)] text-center" data-testid="bk-complete-loading">
        Confirming your payment…
      </div>
    );
  }

  if (error) {
    const isPaymentIncomplete = error === "payment_not_completed";
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-20 text-center" data-testid="bk-complete-error">
        <h1 className="zc-headline text-3xl">Payment not confirmed</h1>
        <p className="text-[var(--zc-text-2)] mt-3">{error}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {!isPaymentIncomplete && (
            <button
              type="button"
              onClick={confirmPayment}
              disabled={completing}
              className="px-5 h-11 zc-btn-primary text-sm disabled:opacity-60"
              data-testid="bk-complete-retry-confirm"
            >
              Retry
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/book/${id}`)}
            className="px-5 h-11 zc-btn-ghost text-sm"
            data-testid="bk-complete-retry"
          >
            Return to checkout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20" data-testid="bk-complete-confirmed">
      <div className="zc-card p-6 sm:p-10 text-center">
        <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(22,163,74,0.12)" }}>
          <CheckCircle2 size={24} className="text-[var(--zc-success)]" />
        </div>
        <div className="eyebrow mt-6">Confirmed</div>
        <h1 className="zc-headline text-4xl mt-2">Your trip is booked.</h1>
        <p className="text-[var(--zc-text-2)] mt-3 max-w-md mx-auto">
          {vehicleLabel || "Vehicle"}
        </p>
        <BookingScheduleSummary booking={confirmed} testId="bk-complete-schedule" />
        {(confirmed.pickup_location || confirmed.dropoff_location) && (
          <div className="mt-6 text-left max-w-sm mx-auto text-sm space-y-2" data-testid="bk-complete-locations">
            {confirmed.pickup_location && (
              <div className="flex gap-2 text-[var(--zc-text-2)]">
                <MapPin size={14} className="text-[var(--zc-accent)] shrink-0 mt-0.5" />
                <div>
                  <div className="eyebrow">Pick-up</div>
                  <div className="text-[var(--zc-text)]">{confirmed.pickup_location}</div>
                </div>
              </div>
            )}
            {confirmed.dropoff_location && confirmed.dropoff_location !== confirmed.pickup_location && (
              <div className="flex gap-2 text-[var(--zc-text-2)]">
                <MapPin size={14} className="text-[var(--zc-accent)] shrink-0 mt-0.5" />
                <div>
                  <div className="eyebrow">Drop-off</div>
                  <div className="text-[var(--zc-text)]">{confirmed.dropoff_location}</div>
                </div>
              </div>
            )}
          </div>
        )}
        {(confirmed.subtotal != null || Number(confirmed.taxes_and_fees || 0) > 0
          || Number(confirmed.one_way_fee || 0) > 0
          || Number(confirmed.after_hours_pickup_fee || 0) > 0
          || Number(confirmed.after_hours_return_fee || 0) > 0) && (
          <div className="mt-6 max-w-xs mx-auto text-sm space-y-1.5 text-left" data-testid="bk-complete-breakdown">
            {confirmed.subtotal != null && (
              <div className="flex justify-between text-[var(--zc-text-2)]">
                <span>Base rental</span>
                <span className="font-semibold text-[var(--zc-text)]">${formatUsd(confirmed.subtotal)}</span>
              </div>
            )}
            {(() => {
              const oneWay = Number(confirmed.one_way_fee || 0);
              const ahPickup = Number(confirmed.after_hours_pickup_fee || 0);
              const ahReturn = Number(confirmed.after_hours_return_fee || 0);
              const taxFees = Number(confirmed.taxes_and_fees || 0);
              const inclusive = inclusiveTaxesAndFees({
                taxesAndFees: taxFees,
                oneWayFee: oneWay,
                afterHoursPickupFee: ahPickup,
                afterHoursReturnFee: ahReturn,
              });
              if (inclusive <= 0) return null;
              const parts = [
                taxFees > 0 && "tax & service",
                oneWay > 0 && "one-way return",
                (ahPickup > 0 || ahReturn > 0) && "after-hours",
              ].filter(Boolean);
              return (
                <div className="flex justify-between text-[var(--zc-text-2)]" data-testid="bk-complete-taxes-fees">
                  <span className="min-w-0">
                    Taxes &amp; fees
                    {parts.length > 1 && (
                      <span className="block zc-meta font-normal mt-0.5 text-[var(--zc-text-3)]">
                        Includes {parts.join(", ")}
                      </span>
                    )}
                  </span>
                  <span className="font-semibold text-[var(--zc-text)]">+${formatUsd(inclusive)}</span>
                </div>
              );
            })()}
            {(Number(confirmed.discount || 0) + Number(confirmed.coupon_discount || 0)) > 0 && (
              <div className="flex justify-between text-[var(--zc-success)]">
                <span>Discount</span>
                <span className="font-semibold">
                  −${formatUsd(Number(confirmed.discount || 0) + Number(confirmed.coupon_discount || 0))}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-10 text-left">
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Booking ID</div>
            <div className="font-mono text-xs">{confirmed.booking_id}</div>
          </div>
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Total</div>
            <div className="zc-price text-2xl">${formatUsd(confirmed.total)}</div>
          </div>
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Payment</div>
            <div className="text-sm flex items-center gap-2 mt-1">
              <span className="dot live" /> Stripe
            </div>
          </div>
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Booked on</div>
            <div className="text-xs text-[var(--zc-text-2)]">{confirmed.created_at ? formatLocalTimestamp(confirmed.created_at) : "—"}</div>
          </div>
        </div>
        <div className="flex justify-center gap-3 mt-10">
          <Link to="/dashboard/renter" className="px-5 h-11 grid place-items-center zc-btn-primary text-sm" data-testid="bk-go-trips">
            View my trips
          </Link>
          <Link to="/browse" className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm" data-testid="bk-keep-browsing">
            Keep browsing
          </Link>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-6 text-left">
        <PolicyCard bookingId={confirmed.booking_id} insurance={confirmed.insurance} />
        <TelematicsPanel bookingId={confirmed.booking_id} isRenter status={confirmed.status} />
      </div>

      {confirmedEsim && (
        <div className="mt-6 text-left" data-testid="bk-esim-block">
          <div className="eyebrow mb-3">Travel Pack · eSIM</div>
          <EsimCard esim={confirmedEsim} onUpdate={async () => {
            try {
              const er = await axios.get(`${API}/esim/mine`, { withCredentials: true });
              const refreshed = pageItems(er.data).find((e) => e.activation_id === confirmedEsim.activation_id);
              if (refreshed) setConfirmedEsim(refreshed);
            } catch (_) {}
          }} />
        </div>
      )}
    </div>
  );
}
